import { cfApi } from "../../api/apiClient";
import type {
  CashflowCategory,
  CashflowSummary,
  CategoryItem,
  Currency,
} from "../../types";
import type { PageScope } from "../../utils/types";

export type DashboardTab = "cashflow" | "receivables";

const DASHBOARD_TAB_ORDER: DashboardTab[] = ["cashflow", "receivables"];

export function getAvailableDashboardTabs(
  scopes: PageScope[] | null | undefined,
): DashboardTab[] {
  const tabs: DashboardTab[] = [];
  if (scopes?.includes("CASHFLOW")) tabs.push("cashflow");
  if (scopes?.includes("RECEIVABLES")) tabs.push("receivables");
  return tabs;
}

export function resolveDashboardTab(
  scopes: PageScope[] | null | undefined,
  storedTab?: DashboardTab | null,
): DashboardTab {
  const available = getAvailableDashboardTabs(scopes);
  if (storedTab && available.includes(storedTab)) return storedTab;
  return available[0] ?? DASHBOARD_TAB_ORDER[0];
}

export function shouldShowDashboardTabs(tabs: DashboardTab[]): boolean {
  return tabs.length > 1;
}

/** Live AR aging — per-currency rows, or one consolidated row in display currency. */
export function liveAgingApiUrl(
  displayCurrency: Currency,
  consolidated: boolean,
): string {
  if (!consolidated) return cfApi("/aging/live");
  const params = new URLSearchParams({
    displayCurrency,
    consolidated: "true",
  });
  return cfApi(`/aging/live?${params.toString()}`);
}

/** Default dashboard cashflow window: ~12 months back through next month (YYYY-MM-DD). */
export function rollingYearRange(): { dateFrom: string; dateTo: string } {
  const end = new Date();
  end.setMonth(end.getMonth() + 1);
  const start = new Date();
  start.setMonth(start.getMonth() - 12);
  return {
    dateFrom: start.toISOString().split("T")[0],
    dateTo: end.toISOString().split("T")[0],
  };
}

/** Combines subcategory totals into their parent so dashboards show top-level expense categories only. */
export function rollupExpenseCategoriesToParentsOnly(
  expenseCategories: CashflowCategory[],
  categories: CategoryItem[],
): CashflowCategory[] {
  const metaById = new Map(categories.map((c) => [c.id, c]));

  function rollupTargetId(catId: string): string {
    const meta = metaById.get(catId);
    if (!meta) return catId;
    return meta.parentCategoryId ?? catId;
  }

  const merged = new Map<string, CashflowCategory>();

  for (const c of expenseCategories) {
    const targetId = rollupTargetId(c.id);
    const parentMeta = metaById.get(targetId);
    const existing = merged.get(targetId);

    if (!existing) {
      merged.set(targetId, {
        id: targetId,
        name: parentMeta?.name ?? c.name,
        color: parentMeta?.color ?? c.color,
        totals: { ...c.totals },
      });
      continue;
    }

    for (const [month, v] of Object.entries(c.totals)) {
      existing.totals[month] = (existing.totals[month] ?? 0) + v;
    }
    if (parentMeta) {
      existing.name = parentMeta.name;
      existing.color = parentMeta.color;
    }
  }

  return Array.from(merged.values());
}

export function sumCategoryTotals(totals: Record<string, number>): number {
  return Object.values(totals).reduce((a, b) => a + b, 0);
}

/**
 * When a merged slice combines several category ids, picks the dominant contributor by summed totals
 * (full-range or rolling-window summaries), e.g. for legend coloring.
 */
export function drillCategoryIdForExpenseSlice(
  sources: CashflowCategory[],
): string {
  if (sources.length === 0) return "";
  if (sources.length === 1) return sources[0].id;
  const ranked = [...sources].sort((a, b) => {
    const dv = sumCategoryTotals(b.totals) - sumCategoryTotals(a.totals);
    if (dv !== 0) return dv;
    return a.id.localeCompare(b.id);
  });
  return ranked[0].id;
}

/**
 * Merges expense rows that share the same label (e.g. two category IDs both named "Cash Out").
 * Sentinel aggregation ids stay distinct. Sets mergeSources when more than one id contributed.
 */
export function mergeExpenseCategoriesByNormalizedName(
  expenseCategories: CashflowCategory[],
): CashflowCategory[] {
  type Bucket = {
    displayName: string;
    totals: Record<string, number>;
    contributors: CashflowCategory[];
  };
  const buckets = new Map<string, Bucket>();

  const contributorSnap = (c: CashflowCategory): CashflowCategory => ({
    ...c,
    totals: { ...c.totals },
    mergeSources: undefined,
  });

  for (const c of expenseCategories) {
    const key =
      c.id === "__uncategorized__" || c.id.startsWith("__")
        ? `__id__:${c.id}`
        : c.name.trim().toLowerCase() || `__id__:${c.id}`;
    const cur = buckets.get(key);
    if (!cur) {
      buckets.set(key, {
        displayName: c.name,
        totals: { ...c.totals },
        contributors: [contributorSnap(c)],
      });
      continue;
    }
    for (const [month, v] of Object.entries(c.totals)) {
      cur.totals[month] = (cur.totals[month] ?? 0) + v;
    }
    cur.contributors.push(contributorSnap(c));
  }

  return Array.from(buckets.values()).map((b) => {
    const contrib = b.contributors;
    const mergedRow: CashflowCategory = {
      id:
        contrib.length > 1
          ? contrib
              .map((x) => x.id)
              .sort()
              .join("|")
          : contrib[0].id,
      name: b.displayName,
      color: contrib[0].color || "#94a3b8",
      totals: { ...b.totals },
      ...(contrib.length > 1 ? { mergeSources: contrib } : {}),
    };
    return mergedRow;
  });
}

/** UTC bounds for YYYY-MM (cashflow month keys). */
export function formatCashflowMonthKeyLabel(monthKey: string): string {
  const parts = monthKey.split("-").map(Number);
  const y = parts[0];
  const mo = parts[1];
  if (!y || !mo) return monthKey;
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Percent change; returns null if undefined or prior is zero. */
export function pctChangeVsPrior(
  current: number,
  prior: number,
): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(prior)) return null;
  if (prior === 0) return null;
  return ((current - prior) / Math.abs(prior)) * 100;
}

export function formatPctSigned(pct: number | null): string {
  if (pct === null || !Number.isFinite(pct)) return "–";
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

/** Last two calendar months in periods (already sorted ascending). */
export function lastTwoMonthMetrics(summary: CashflowSummary | undefined): {
  currentMonthLabel: string | null;
  balanceEnd: number | null;
  incomeMom: number | null;
  expenseMom: number | null;
  incomeVsPriorPct: number | null;
  expenseVsPriorPct: number | null;
  balanceMomPct: number | null;
} {
  const periods = summary?.periods ?? [];
  if (periods.length === 0) {
    return {
      currentMonthLabel: null,
      balanceEnd: null,
      incomeMom: null,
      expenseMom: null,
      incomeVsPriorPct: null,
      expenseVsPriorPct: null,
      balanceMomPct: null,
    };
  }
  const last = periods[periods.length - 1];
  const prior = periods.length >= 2 ? periods[periods.length - 2] : null;
  const months = summary?.months ?? [];
  const lastKey = months[months.length - 1];
  const ending = lastKey ? summary?.endingBalances?.[lastKey] : undefined;

  return {
    currentMonthLabel: last?.month ?? null,
    balanceEnd:
      typeof ending === "number" ? ending : (last?.runningTotal ?? null),
    incomeMom: last?.income ?? null,
    expenseMom: last?.expenses ?? null,
    incomeVsPriorPct:
      prior !== null ? pctChangeVsPrior(last.income, prior.income) : null,
    expenseVsPriorPct:
      prior !== null ? pctChangeVsPrior(last.expenses, prior.expenses) : null,
    balanceMomPct:
      prior !== null
        ? pctChangeVsPrior(last.runningTotal, prior.runningTotal)
        : null,
  };
}

export function downloadDashboardCsv(summary: CashflowSummary, label: string) {
  const rows = summary.periods ?? [];
  const header = ["month", "income", "expenses", "net", "running_total"];
  const lines = [
    header.join(","),
    ...rows.map((p) =>
      [p.month, p.income, p.expenses, p.net, p.runningTotal].join(","),
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${label}-cashflow-summary.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
